import React, { useCallback, useMemo, useState } from "react";
import { ReactSVG } from "react-svg";
import { useMutation } from "@apollo/client";

// atoms
import { ModuleCheckbox } from "@atoms/index";

// icons
import {
  ConcretingStatusCanceled,
  ConcretingStatusCompleted,
  ConcretingStatusInProgress,
  ConcretingStatusProgrammed,
  CertificateStatus,
  ContractStatus,
  ConcretingResumeCanceled,
  ConcretingResumeDelivered,
  ConcretingResumeDischarging,
  ConcretingResumeInConstruction,
  ConcretingResumeInTransit,
  ConcretingResumeNFSE,
  ConcretingResumePreparing,
  ConcretingResumeProgrammed
} from "@assets/icons";

// hooks
import { useAuth } from "@hooks/useAuth";

// languages
import useTranslation from "@languages/useTranslation";
import { TLanguages } from "@languages/languageData";
import { BaseCardMessages, CustomerMessages } from "@languages/interfaces";

// Telluria
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";

// helpers
import { currencyFormat } from "@helpers/index";
import utils from "@helpers/utils";

// store
import { ModulesID } from "@store/constants/index";

// graphql
import Store, { ContractPermissions } from "@store/jotai";
import {
  ContractType,
  ConcretingType,
  InvoiceType,
  ConcreteStrengthCertificateType,
  ClientType,
  UserType,
  PermissionUserMutationsDeleteArgs,
  PermissionUserMutationsCreateArgs,
  ECommandResultStatus,
  ConcretingRequestType,
  TravelType,
  Maybe,
  ETravelStatus,
  EConcretingStatus
} from "@store/graphql/helpers/types.graphql";
import {
  permissionUserDelete,
  TPermissionUserDeleteMutations,
  permissionUserCreate,
  TPermissionUserCreateMutations
} from "@graphql/groups/permissionUser/mutation.graphql";

import * as Styled from "./styles";

interface ConcretingCardType {
  concreting: ConcretingType;
  travels: Maybe<Maybe<TravelType>[]> | undefined;
}

interface BaseCardHeaderProps {
  type:
    | "contract"
    | "concreting"
    | "invoice"
    | "certificate"
    | "client"
    | "user"
    | "permissionUser"
    | "concretingRequest"
    | "concretingApproval";
  data:
    | ContractType
    | ConcretingCardType
    | InvoiceType
    | ConcreteStrengthCertificateType
    | ClientType
    | ContractPermissions
    | UserType
    | ConcretingRequestType;
}

interface IIcons {
  [key: string]: {
    icon?: string;
    color: string;
  };
}

interface IBaseCardHeaderIcons {
  contract: IIcons;
  concreting: IIcons;
  invoice: IIcons;
  permissionUser: IIcons;
  concretingRequest: IIcons;
}

const baseCardIcons: IBaseCardHeaderIcons = {
  contract: {
    ACTIVE: { color: "#64DD17" },
    PENDING: { color: "#FF0000" },
    INACTIVE: { color: "#A3A3A3" }
  },
  concreting: {
    PROGRAMMED: { icon: ConcretingStatusProgrammed, color: "#64DD17" },
    IN_PROGRESS: { icon: ConcretingStatusInProgress, color: "#64DD17" },
    COMPLETED: { icon: ConcretingStatusCompleted, color: "#64DD17" },
    CANCELED: { icon: ConcretingStatusCanceled, color: "#FF0000" }
  },
  invoice: {
    OPEN: { color: "#64DD17" },
    PAID: { color: "#A3A3A3" },
    OVERDUE: { color: "#FF0000" }
  },
  permissionUser: {
    ACTIVE: { color: "#64DD17" },
    PENDING: { color: "#FF0000" },
    INACTIVE: { color: "#A3A3A3" }
  },
  concretingRequest: {
    APPROVED: { color: "#64DD17" },
    REJECTED: { color: "#FF0000" },
    PENDING: { color: "#A3A3A3" }
  }
};

const BaseCardHeader: React.FC<BaseCardHeaderProps> = ({ type, data }) => {
  const { t, i18n } = useTranslation();
  const { addToast } = useToast();
  const { getDecodedToken } = useAuth();
  const token = getDecodedToken();
  const [currentData, setCurrentData] = useState(data);

  const { filteredPermissionsUsers, setFilteredPermissionsUsers } = Store.usePermissionsUsers();

  // mutations
  const [MutationDeletePermissionUser] = useMutation<
    TPermissionUserDeleteMutations,
    PermissionUserMutationsDeleteArgs
  >(permissionUserDelete());

  const [MutationCreatePermissionUser] = useMutation<
    TPermissionUserCreateMutations,
    PermissionUserMutationsCreateArgs
  >(
    permissionUserCreate([
      "id",
      "contract.id",
      "contract.code",
      "contract.status",
      "contract.contractDate",
      "contract.construction.name",
      "contract.construction.name",
      "contract.construction.address",
      "contract.construction.county",
      "contract.construction.number",
      "contract.construction.complement",
      "contract.construction.client.name",
      "module.id",
      "module.description"
    ])
  );
  const objectInUse = useMemo(() => {
    if (type === "contract" || type === "permissionUser") {
      return data as ContractType;
    }

    if (type === "concreting") {
      const { concreting } = data as ConcretingCardType;

      return concreting as ConcretingType;
    }

    if (type === "concretingRequest") {
      return data as ConcretingRequestType;
    }

    return data as InvoiceType;
  }, [type, data]);

  const iconContainer = useMemo(() => {
    if (type === "certificate") {
      return (
        <Styled.IconContainer color="#64DD17">
          <ReactSVG src={CertificateStatus} />
        </Styled.IconContainer>
      );
    }

    if (type === "permissionUser") {
      const { status } = objectInUse;
      const { color } = baseCardIcons[type][status];

      return (
        <Styled.IconContainer color={color}>
          <ReactSVG src={ContractStatus} />
        </Styled.IconContainer>
      );
    }

    if (type === "client") {
      if (!data) return null;
      const client = data as ClientType;

      const color = client.pendency ? "#FF0000" : "#64DD17";

      return (
        <Styled.IconContainer color={color}>
          <Styled.BarStatus className="barStatusColor" />
        </Styled.IconContainer>
      );
    }

    if (type === "user") {
      if (!data) return null;
      const user = data as UserType;

      const color = user.active ? user?.isValidated ? "#64DD17" : "#FF0000" : "#A3A3A3";

      return (
        <Styled.IconContainer color={color}>
          <Styled.BarStatus className="barStatusColor" />
        </Styled.IconContainer>
      );
    }

    const { icon, color } = baseCardIcons[type][objectInUse.status];

    return (
      <Styled.IconContainer color={color}>
        {icon ? (
          <ReactSVG src={icon} />
        ) : (
          <Styled.BarStatus className="barStatusColor" />
        )}
      </Styled.IconContainer>
    );
  }, [type, objectInUse, data]);

  const contentContract = useMemo(() => {
    if (type !== "contract") return null;
    const contract = data as ContractType;

    const addressFormated = `${contract.construction?.address}, ${
      contract.construction?.number ? contract.construction?.number : "S/N"
    } ${
      contract.construction?.complement
        ? `, ${contract.construction.complement}`
        : ""
    } ${
      contract.construction?.county ? `, ${contract.construction?.county}` : ""
    }`;

    const sellersFormatted = contract.contractSellers?.reduce(
      (previous, contractSeller, index) => {
        const sellerName = contract.contractSellers
          && index === contract.contractSellers.length - 1
          ? contractSeller?.seller?.name
          : `${contractSeller?.seller?.name} | `;

        return previous + sellerName;
      },
      ""
    );

    return (
      <div>
        <div className="titleHeader">{contract.construction?.name}</div>
        <div>{addressFormated}</div>
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.contractLabelText)}`}:{" "}
          </span>{" "}
          {contract.code}
          &nbsp;&nbsp;&nbsp;
          <span className="titleContent">
            {`${t(BaseCardMessages.contractDateText)}`}:{" "}
          </span>{" "}
          {new Date(contract.contractDate).toLocaleDateString()}
        </div>
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.contractSellerText)}`}:{" "}
          </span>{" "}
          {sellersFormatted}
        </div>
      </div>
    );
  }, [data, t, type]);

  const contentConcreting = useMemo(() => {
    if (type !== "concreting") return null;
    const { concreting } = data as ConcretingCardType;

    return (
      <div>
        <div className="titleHeader">
          {concreting.contract?.construction?.name}
        </div>
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.travelProgrammedText)}`}:{" "}
          </span>
          {utils.formatDateIfHave(
            concreting.scheduledDate,
            "fullDateWithoutSeconds"
          )}
        </div>
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.volumeText)}`}:{" "}
          </span>
          {`${concreting.deliveredVolume} / ${concreting.programmedVolume} m³`}
        </div>
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.concreteRecipeText)}`}:{" "}
          </span>
          {concreting.contractConcreteRecipe?.concreteRecipe?.description}
        </div>
      </div>
    );
  }, [data, t, type]);

  const concretingTravelsResume = useMemo(() => {
    if (type !== "concreting") return null;
    const { concreting } = data as ConcretingCardType;

    // Calc of data of resume
    const resumeInfoByStatus = (status: ETravelStatus) => {
      if (concreting.status === EConcretingStatus.CANCELED) {
        return "0 · 0 m3";
      }
      const travelsByStatus = concreting.travels?.filter((travel) => travel?.status === status);

      const volumeByStatus = Number(travelsByStatus?.reduce((acc, travel) => acc + Number(travel?.volume), 0)).toLocaleString("pt-BR");

      return `${travelsByStatus?.length} · ${volumeByStatus} m3`;
    };

    return (
      <Styled.ConcretingResume>
        <div className="concretingResumeColumn">
          <div className="resumeItem">
            <div>
              <ReactSVG className="resumeIcon" src={ConcretingResumeProgrammed} />
              <span className="titleContent">
                {t(BaseCardMessages.concretingResumeProgrammed)}:{" "}
              </span>
            </div>
            {resumeInfoByStatus(ETravelStatus.PROGRAMMED)}
          </div>
          <div className="resumeItem">
            <div>
              <ReactSVG className="resumeIcon" src={ConcretingResumeNFSE} />
              <span className="titleContent">
                {t(BaseCardMessages.concretingResumeNFSE)}:{" "}
              </span>
            </div>
            {resumeInfoByStatus(ETravelStatus.INVOICE_ISSUED)}
          </div>
          <div className="resumeItem">
            <div>
              <ReactSVG className="resumeIcon" src={ConcretingResumeInTransit} />
              <span className="titleContent">
                {t(BaseCardMessages.concretingResumeInTransit)}:{" "}
              </span>
            </div>
            {resumeInfoByStatus(ETravelStatus.IN_TRANSIT)}
          </div>
          <div className="resumeItem">
            <div>
              <ReactSVG className="resumeIcon" src={ConcretingResumeInConstruction} />
              <span className="titleContent">
                {t(BaseCardMessages.concretingResumeInConstruction)}:{" "}
              </span>
            </div>
            {resumeInfoByStatus(ETravelStatus.IN_CONSTRUCTION)}
          </div>
        </div>
        <div className="concretingResumeColumn">
          <div className="resumeItem">
            <div>
              <ReactSVG className="resumeIcon" src={ConcretingResumeDischarging} />
              <span className="titleContent">
                {t(BaseCardMessages.concretingResumeDischarging)}:{" "}
              </span>
            </div>
            {resumeInfoByStatus(ETravelStatus.DISCHARGING)}
          </div>
          <div className="resumeItem">
            <div>
              <ReactSVG className="resumeIcon" src={ConcretingResumeDelivered} />
              <span className="titleContent">
                {t(BaseCardMessages.concretingResumeDelivered)}:{" "}
              </span>
            </div>
            {resumeInfoByStatus(ETravelStatus.DELIVERED)}
          </div>
          <div className="resumeItem">
            <div>
              <ReactSVG className="resumeIcon" src={ConcretingResumePreparing} />
              <span className="titleContent">
                {t(BaseCardMessages.concretingResumePreparing)}:{" "}
              </span>
            </div>
            {resumeInfoByStatus(ETravelStatus.PREPARING)}
          </div>
          <div className="resumeItem">
            <div>
              <ReactSVG className="resumeIcon" src={ConcretingResumeCanceled} />
              <span className="titleContent">
                {t(BaseCardMessages.concretingResumeCanceled)}:{" "}
              </span>
            </div>
            {resumeInfoByStatus(ETravelStatus.CANCELED)}
          </div>
        </div>
      </Styled.ConcretingResume>
    );
  }, [data, t, type]);

  const contentInvoice = useMemo(() => {
    if (type !== "invoice") return null;
    const invoice = data as InvoiceType;

    const invoiceInstallmentTotal = invoice.invoiceInstallments
      ? invoice.invoiceInstallments.reduce((previous, invoiceInstallment) => previous + (invoiceInstallment?.netValue || 0), 0)
      : 0;

    const invoiceInstallmentTotalFormatted = currencyFormat(
      invoiceInstallmentTotal,
      i18n.language as TLanguages
    );

    return (
      <div>
        <div className="titleHeader">
          {invoice.contract?.construction?.name}
        </div>
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.invoiceContractLabelText)}`}:{" "}
          </span>
          {invoice.contract?.code}
          &nbsp;&nbsp;&nbsp;
          <span className="titleContent">
            {`${t(BaseCardMessages.invoiceContractDateText)}`}:{" "}
          </span>
          {new Date(invoice.issueDate).toLocaleDateString()}
        </div>
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.invoiceNumberText)}`}:{" "}
          </span>
          {invoice.invoiceNumber}
          {invoice.fiscalDocument && (
            <>
              &nbsp;&nbsp;&nbsp;
              <span className="titleContent">
                {`${t(BaseCardMessages.invoiceDocumentTypeText)}`}:{" "}
              </span>
              {invoice.fiscalDocument.fiscalDocumentNumber}
            </>
          )}
        </div>
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.invoiceTotalValueText)}`}:{" "}
          </span>
          {invoiceInstallmentTotalFormatted}
        </div>
      </div>
    );
  }, [data, i18n.language, t, type]);

  const contentCertificate = useMemo(() => {
    if (type !== "certificate") return null;
    const certificate = data as ConcreteStrengthCertificateType;

    return (
      <div>
        <div className="titleHeader">
          {certificate.concreting?.contract?.construction?.name}
        </div>
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.certificateContractText)}`}:{" "}
          </span>
          {certificate.concreting?.contract?.code}
        </div>
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.certificateConcretingDateText)}`}:{" "}
          </span>
          {new Date(certificate.concreting?.scheduledDate).toLocaleDateString()}
        </div>
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.certificateDateText)}`}:{" "}
          </span>
          {new Date(certificate.issueDate).toLocaleDateString()}
        </div>
      </div>
    );
  }, [data, t, type]);

  const contentClient = useMemo(() => {
    if (type !== "client") return null;
    const client = data as ClientType;

    const creditLimitFormatted = client.creditLimit
      && currencyFormat(client.creditLimit, i18n.language as TLanguages);

    const showCreditLimitAvailable = !!client.creditLimit && !!client.creditLimitUsed;
    const creditLimitAvailable = Number(client.creditLimit) - Number(client.creditLimitUsed);
    const creditLimitAvailableFormated = currencyFormat(Number(creditLimitAvailable), i18n.language as TLanguages);

    return (
      <div>
        <div className="titleHeader">{client.alias}</div>
        <div>{client.name}</div>
        {(client.creditLimit || client.creditLimit === 0) && (
          <div>
            <span className="titleContent">
              {`${t(BaseCardMessages.clientCreditLimitText)}`}:{" "}
            </span>
            <div className="creditLimitValue">{creditLimitFormatted}</div>
          </div>
        )}
        { showCreditLimitAvailable && (
          <div>
            <span className="titleContent">
              {`${t(BaseCardMessages.clientCreditLimitAvailableText)}`}:{" "}
            </span>
            {creditLimitAvailableFormated}
          </div>
        )}
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.clientDocumentText)}`}:{" "}
          </span>
          {client.document}
        </div>
        {client.pendency && (
          <>
            <br />
            <div className="pendencyContent">
              <span className="titleContent">
                {`${t(BaseCardMessages.clientPendencyText)}`}:{" "}
              </span>
              {client.pendency}
            </div>
          </>
        )}
      </div>
    );
  }, [data, i18n.language, t, type]);

  const contentUser = useMemo(() => {
    if (type !== "user") return null;
    const user = data as UserType;

    return (
      <div>
        <div className="titleHeader">{user.fullName}</div>
        <div>{user.role ? user.role : "(Sem função definida)"}</div>
        <div>{user.email}</div>
      </div>
    );
  }, [data, type]);

  const createPermissionUser = useCallback(
    async (contractPermission: ContractPermissions, module: string) => {
      const constructionId = contractPermission.construction?.id;

      if (!constructionId) return null;

      const mutation = await MutationCreatePermissionUser({
        variables: {
          permissionUser: {
            userId: contractPermission.userId,
            contractId: contractPermission.id,
            constructionId,
            moduleId: ModulesID[module],
            statusChangeUserId: token.primarysid,
            statusChangeDate: new Date()
          }
        }
      });

      return mutation.data?.permissionUser.create;
    },
    [MutationCreatePermissionUser, token]
  );

  const updateFilteredPermissionsUsers = useCallback(
    (permissionUser: ContractPermissions) => {
      const newPermissionsUsers = filteredPermissionsUsers.map((permission) => {
        if (permission.id === permissionUser.id) {
          return permissionUser;
        }

        return permission;
      });

      setFilteredPermissionsUsers(newPermissionsUsers);
    },
    [filteredPermissionsUsers, setFilteredPermissionsUsers]
  );

  const handleChangeCheckbox = useCallback(
    async (checked: boolean, module: string) => {
      const contractPermission = currentData as ContractPermissions;

      if (checked) {
        try {
          const response = await createPermissionUser(
            contractPermission,
            module
          );

          if (response?.status === ECommandResultStatus.SUCCESS) {
            const newPermission = response.result;

            if (!newPermission) return;

            const newPermissions = contractPermission?.permissions || [];

            newPermissions.push(newPermission);

            const newCurrentData = {
              ...contractPermission,
              permissions: newPermissions
            };

            setCurrentData(newCurrentData);
            updateFilteredPermissionsUsers(newCurrentData);
          }
        } catch (error) {
          addToast({
            type: "error",
            title: "Internal client error",
            description: error.message
          });
        }
      } else {
        const permissionToDelete = contractPermission?.permissions?.find(
          (permission) => permission?.contract?.id === contractPermission?.id
            && permission?.module?.id === ModulesID[module]
        );

        if (!permissionToDelete) return;

        const newPermissions = contractPermission?.permissions?.filter(
          (permission) => permission?.module?.id !== ModulesID[module]
        );

        try {
          const mutation = await MutationDeletePermissionUser({
            variables: { id: permissionToDelete.id }
          });
          const response = mutation.data?.permissionUser.delete;

          if (response?.status === ECommandResultStatus.SUCCESS) {
            const newCurrentData = {
              ...currentData,
              permissions: newPermissions
            } as ContractPermissions;

            setCurrentData(newCurrentData);

            updateFilteredPermissionsUsers(newCurrentData);
          }
        } catch (error) {
          addToast({
            type: "error",
            title: "Internal client error",
            description: error.message
          });
        }
      }
    },
    [
      currentData,
      MutationDeletePermissionUser,
      addToast,
      createPermissionUser,
      updateFilteredPermissionsUsers
    ]
  );

  const contentPermissionUser = useMemo(() => {
    if (type !== "permissionUser") return null;
    const contractPermission = currentData as ContractPermissions;

    const addressFormated = `${contractPermission.construction?.address}, ${
      contractPermission.construction?.number
        ? contractPermission.construction?.number
        : "S/N"
    } ${
      contractPermission.construction?.complement
        ? `, ${contractPermission.construction.complement}`
        : ""
    } ${
      contractPermission.construction?.county
        ? `, ${contractPermission.construction?.county}`
        : ""
    }`;

    const contractModulePermission = contractPermission.permissions?.filter(
      (permission) => permission?.module?.id === ModulesID.CONTRACT
    );
    const hasContractPermission = (contractModulePermission?.length || 0) > 0;

    const concretingModulePermission = contractPermission.permissions?.filter(
      (permission) => permission?.module?.id === ModulesID.CONCRETING
    );
    const hasConcretingPermission = (concretingModulePermission?.length || 0) > 0;

    const invoiceModulePermission = contractPermission.permissions?.filter(
      (permission) => permission?.module?.id === ModulesID.INVOICE
    );
    const hasInvoicePermission = (invoiceModulePermission?.length || 0) > 0;

    const certificateModulePermission = contractPermission.permissions?.filter(
      (permission) => permission?.module?.id === ModulesID.CERTIFICATE
    );
    const hasCertificatePermission = (certificateModulePermission?.length || 0) > 0;

    const concretingRequestModulePermission = contractPermission.permissions?.filter(
      (permission) => permission?.module?.id === ModulesID.CONCRETING_REQUEST
    );
    const hasConcretingRequestPermission = (concretingRequestModulePermission?.length || 0) > 0;

    const clientModulePermission = contractPermission.permissions?.filter(
      (permission) => permission?.module?.id === ModulesID.CLIENT
    );
    const hasClientPermission = (clientModulePermission?.length || 0) > 0;

    const amountsContractPermission = contractPermission.permissions?.filter(
      (permission) => permission?.module?.id === ModulesID.AMOUNTS
    );
    const hasAmountContractPermission = (amountsContractPermission?.length || 0) > 0;

    return (
      <div>
        <div className="titleHeader">
          {contractPermission.construction?.name}
        </div>
        <div>{addressFormated}</div>
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.permissionClientNameText)}`}:{" "}
          </span>{" "}
          {contractPermission.construction?.client?.name}
        </div>
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.permissionContractText)}`}:{" "}
          </span>{" "}
          {contractPermission.code}
          &nbsp;&nbsp;&nbsp;
          <span className="titleContent">
            {`${t(BaseCardMessages.permissionDataText)}`}:{" "}
          </span>{" "}
          {new Date(contractPermission.contractDate).toLocaleDateString()}
        </div>
        <div className="checkboxContent">
          <ModuleCheckbox
            title={t(CustomerMessages.applicationContract)}
            checked={hasContractPermission}
            module="CONTRACT"
            handleChangeCheckbox={handleChangeCheckbox}
          />
          <ModuleCheckbox
            title={t(CustomerMessages.applicationInvoice)}
            checked={hasInvoicePermission}
            module="INVOICE"
            handleChangeCheckbox={handleChangeCheckbox}
          />
          <ModuleCheckbox
            title={t(CustomerMessages.applicationConcreting)}
            checked={hasConcretingPermission}
            module="CONCRETING"
            handleChangeCheckbox={handleChangeCheckbox}
          />
        </div>
        <div className="checkboxContent">
          <ModuleCheckbox
            title={t(CustomerMessages.applicationContractAmounts)}
            checked={hasAmountContractPermission}
            module="AMOUNTS"
            handleChangeCheckbox={handleChangeCheckbox}
          />
          <ModuleCheckbox
            title={t(CustomerMessages.applicationCertificate)}
            checked={hasCertificatePermission}
            module="CERTIFICATE"
            handleChangeCheckbox={handleChangeCheckbox}
          />
          <ModuleCheckbox
            title={t(CustomerMessages.applicationConcretingRequestAbbreviated)}
            checked={hasConcretingRequestPermission}
            module="CONCRETING_REQUEST"
            handleChangeCheckbox={handleChangeCheckbox}
          />
        </div>
        <div className="checkboxContent">

          <ModuleCheckbox
            title={t(CustomerMessages.applicationClient)}
            checked={hasClientPermission}
            module="CLIENT"
            handleChangeCheckbox={handleChangeCheckbox}
          />
        </div>
      </div>
    );
  }, [type, currentData, t, handleChangeCheckbox]);

  const contentConcretingRequest = useMemo(() => {
    if (type !== "concretingRequest") return null;
    const concretingRequest = data as ConcretingRequestType;

    const addressFormated = `${
      concretingRequest.contract?.construction?.address
    }, ${
      concretingRequest.contract?.construction?.number
        ? concretingRequest.contract.construction?.number
        : "S/N"
    } ${
      concretingRequest.contract?.construction?.complement
        ? `, ${concretingRequest.contract.construction.complement}`
        : ""
    } ${
      concretingRequest.contract?.construction?.county
        ? `, ${concretingRequest.contract.construction?.county}`
        : ""
    }`;

    const sellersFormatted = concretingRequest.contract?.contractSellers?.reduce(
      (previous, contractSeller, index) => {
        const sellerName = concretingRequest.contract?.contractSellers
            && index === concretingRequest.contract.contractSellers.length - 1
          ? contractSeller?.seller?.name
          : `${contractSeller?.seller?.name} | `;

        return previous + sellerName;
      },
      ""
    );

    return (
      <div>
        <div className="titleHeader">
          {concretingRequest.contract?.construction?.name}
        </div>
        <div>{addressFormated}</div>
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.contractLabelText)}`}:{" "}
          </span>{" "}
          {concretingRequest.contract?.code}
          &nbsp;&nbsp;&nbsp;
          <span className="titleContent">
            {`${t(BaseCardMessages.contractDateText)}`}:{" "}
          </span>{" "}
          {new Date(concretingRequest.requestedDate).toLocaleDateString()}
        </div>
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.contractSellerText)}`}:{" "}
          </span>{" "}
          {sellersFormatted}
        </div>
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.concreteRecipeText)}`}:{" "}
          </span>{" "}
          {
            concretingRequest.contractConcreteRecipe?.concreteRecipe
              ?.description
          }
        </div>
        <div>
          <span className="titleContent">
            {`${t(
              BaseCardMessages.concretingRequestConcreteRecipeRequestedQuantityText
            )}`}
            :{" "}
          </span>{" "}
          {`${concretingRequest.requestedQuantity} m³`}
        </div>
      </div>
    );
  }, [data, type, t]);

  const contentConstructor = useMemo(() => {
    if (type === "contract") {
      return contentContract;
    }

    if (type === "concreting") {
      return contentConcreting;
    }

    if (type === "invoice") {
      return contentInvoice;
    }

    if (type === "certificate") {
      return contentCertificate;
    }

    if (type === "client") {
      return contentClient;
    }

    if (type === "permissionUser") {
      return contentPermissionUser;
    }

    if (type === "concretingRequest") {
      return contentConcretingRequest;
    }

    return contentUser;
  }, [
    type,
    contentContract,
    contentCertificate,
    contentConcreting,
    contentInvoice,
    contentClient,
    contentUser,
    contentPermissionUser,
    contentConcretingRequest
  ]);

  return (
    <Styled.Container>
      <Styled.Content>
        <div className="mainInfoRow">
          {iconContainer}

          <Styled.InfoContainer>{contentConstructor}</Styled.InfoContainer>
        </div>

        {type === "concreting" && (
          <Styled.InfoContainer>
            {concretingTravelsResume}
          </Styled.InfoContainer>
        )}
      </Styled.Content>
    </Styled.Container>
  );
};

export default BaseCardHeader;
